import { type OrganizationBilling, PlanStatus } from '../entity/api/organization-billing';
import { type OrganizationUser, OrganizationUserRole } from '../entity/api/organization-user';
import { type User } from '../entity/api/user';
import { getQueryClient } from '../sdk/request';
import {
  getOrganizationFromCache,
  getOrganizationsListFromCache,
} from '../services/storage/organizations-queries';

export function getSelectedOrganization(currentUser?: User | null, preferredOrgId?: string) {
  const queryClient = getQueryClient();

  // check preferred first
  if (preferredOrgId != null) {
    const org = getOrganizationFromCache(queryClient, preferredOrgId);
    if (org != null) {
      return org;
    }
  }

  const selectedOrgId = currentUser?.selectedOrganizationId ?? 'not_setup';
  const org = getOrganizationFromCache(queryClient, selectedOrgId);
  if (org != null) {
    return org;
  }

  const orgsList = getOrganizationsListFromCache(queryClient);
  return orgsList?.[0];
}

export function getUserOwnerOrgs(
  user?: User | null,
  orgUsers?: (OrganizationUser | undefined | null)[]
): string[] {
  if (user == null || orgUsers == null) {
    return [];
  }

  const ownerOrgs: string[] = [];
  for (let i = 0; i < orgUsers.length; i += 1) {
    const orgUser = orgUsers[i];
    if (orgUser == null || orgUser.role !== OrganizationUserRole.Owner) {
      continue;
    }

    if (orgUser.userId === user._id || orgUser.userEmail === user.email) {
      ownerOrgs.push(orgUser.organizationId);
    }
  }

  return ownerOrgs;
}

export function ownerOrgsHaveOtherUsers(
  currentUser?: User | null,
  orgUsers?: (OrganizationUser | undefined | null)[]
): string[] {
  if (currentUser == null || orgUsers == null) {
    return [];
  }

  const isOwnerOrgs = getUserOwnerOrgs(currentUser, orgUsers);
  const orgsWithUsersIds = [] as string[];

  for (let i = 0; i < orgUsers.length; i += 1) {
    const orgUser = orgUsers[i];

    // ignore all org users that are not the auth user
    if (
      orgUser == null ||
      currentUser?._id == null ||
      orgUser.userId === currentUser._id ||
      orgUser.userEmail === currentUser.email
    ) {
      continue;
    }

    const has = isOwnerOrgs.some((orgId) => orgId === orgUser.organizationId);
    if (has) {
      orgsWithUsersIds.push(orgUser.organizationId);
    }
  }

  return orgsWithUsersIds;
}

export function canOrgChangeArticles(organizationBilling: OrganizationBilling | null): boolean {
  if (organizationBilling == null) {
    return false;
  }

  if (
    organizationBilling.currentPlanStatus !== PlanStatus.NotSubscribed &&
    organizationBilling.currentPlanStatus !== PlanStatus.Cancelled
  ) {
    return true;
  }

  return organizationBilling.hasFreeArticleCreated !== true;
}

export function canOrgGenerateAudit(
  organizationBilling: OrganizationBilling | null | undefined
): boolean {
  if (organizationBilling == null) {
    return false;
  }

  if (
    organizationBilling.currentPlanStatus !== PlanStatus.NotSubscribed &&
    organizationBilling.currentPlanStatus !== PlanStatus.Cancelled
  ) {
    return true;
  }

  return organizationBilling.hasFreeAuditCreated !== true;
}
